import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Mount once near the router root. On every route change:
 *   - If the new URL has a hash (e.g. /#trust-strip), scroll to that element.
 *   - Otherwise, jump to the top of the page.
 *
 * This lets us deep-link back to specific homepage sections (like the trust
 * badge grid that drives /experience/* navigation) without dumping users at
 * the headline.
 */
export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Defer one frame so the new route has mounted and the target id exists.
      const id = hash.replace(/^#/, "");
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
          return true;
        }
        return false;
      };
      // First try immediately, then on next paint, then once more after 120ms
      // for pages that hydrate async data.
      if (tryScroll()) return;
      requestAnimationFrame(() => {
        if (tryScroll()) return;
        setTimeout(tryScroll, 120);
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
